import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import {
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PageTitle from "../components/PageTitle";
import MotivationSchemePopup from "../components/MotivationSchemePopup";
import apiClient from "../utils/apiClient";
import MotivationSchemeEnum from "../enum/MotivationScheme.enum";
import Provider from "../enum/Provider.enum";
import { useSelector } from "react-redux";

const HeadTabMenu = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 20px;
  gap: 10px;

  & > div:not(:last-child) {
    margin-inline-end: 10px;
  }
`;

const Rates = () => {
  const { t } = useTranslation();

  const [schemes, setSchemes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const provider = useSelector((state) => state.app.provider);

  const motivationSchemeLabels = {
    [MotivationSchemeEnum.DEFAULT]: t("rates.motivationScheme.default"),
    [MotivationSchemeEnum.REFERRAL]: t("rates.motivationScheme.referral"),
  };

  const load = async () => {
    setIsLoading(true);

    try {
      const res = await apiClient.get("/data/GetHandbookMotivations");

      if (res.data.success) {
        setSchemes(res.data.select);
      }
    } catch (e) {
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const toggleHidden = async (row) => {
    if (row.is_used) return;

    const confirmed = window.confirm(t("rates.confirmDelete"));

    if (!confirmed) return;

    setIsLoading(true);

    await apiClient.post("/data/SaveMotivationScheme", {
      id: row.id,
      description: row.description,
      is_hidden: !row.is_hidden,
      type: row.type,
      parts: [],
    });

    void load();
  };

  return (
    <>
      <Helmet>
        <title>
          {t("rates.pageTitle")} | {t("common.partnerCabinet")}
        </title>
        <meta name="description" content={t("rates.metaDescription")} />
      </Helmet>

      <PageTitle>{t("rates.pageTitleFull")}</PageTitle>

      <HeadTabMenu>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditing(null);
            setOpen(true);
          }}
          fullWidth
          disabled={isLoading}
        >
          {t("rates.newScheme")}
        </Button>

        <Button
          variant="outlined"
          color="secondary"
          onClick={load}
          fullWidth
          loading={isLoading}
        >
          {t("common.refresh")}
        </Button>
      </HeadTabMenu>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t("rates.description")}</TableCell>

            {provider === Provider.BELARUS ? (
              <TableCell>{t("rates.type")}</TableCell>
            ) : (
              ["broker", "retention", "attraction"].map((mt) => (
                <React.Fragment key={mt}>
                  <TableCell align="center">
                    {t("rates.agentPercent", {
                      type: t(`motivationSchemePopup.mtOptions.${mt}`),
                    })}
                  </TableCell>

                  <TableCell align="center">
                    {t("rates.managerPercent", {
                      type: t(`motivationSchemePopup.mtOptions.${mt}`),
                    })}
                  </TableCell>
                </React.Fragment>
              ))
            )}

            <TableCell>{t("common.actions")}</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {schemes.map((row) => (
            <TableRow key={row.id}>
              <TableCell>{row.description}</TableCell>

              {provider === Provider.BELARUS ? (
                <TableCell>
                  {motivationSchemeLabels?.[row.type] ?? row.type}
                </TableCell>
              ) : (
                ["broker", "retention", "attraction"].map((mt) => {
                  const det = row.details?.[mt] || {};

                  return (
                    <React.Fragment key={mt}>
                      <TableCell align="center">{det.a ?? 0}</TableCell>
                      <TableCell align="center">{det.M ?? 0}</TableCell>
                    </React.Fragment>
                  );
                })
              )}

              <TableCell>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setEditing(row);
                      setOpen(true);
                    }}
                    disabled={isLoading}
                  >
                    {t("common.edit")}
                  </Button>

                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      toggleHidden(row);
                    }}
                    disabled={isLoading || row.is_used}
                  >
                    {t("common.delete")}
                  </Button>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {open && (
        <MotivationSchemePopup
          open={open}
          scheme={editing}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            load();
          }}
        />
      )}
    </>
  );
};

export default Rates;
